import React, { useState } from 'react';
import { HelpCircle, MessageCircle, FileText, AlertCircle, Send } from 'lucide-react';
import './AccountSections.css';

const SupportSection = ({ userId }) => {
  const [activeTab, setActiveTab] = useState('help');
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: '',
    priority: 'normal'
  });

  const faqItems = [
    {
      question: 'How do I create a campaign?',
      answer: 'Navigate to your dashboard and click on "Create Campaign". Fill in the campaign details, set your budget, and publish it to start receiving bids from creators.'
    },
    {
      question: 'How does billing work?',
      answer: 'You are billed monthly based on your subscription plan. You can view and download invoices in the Billing section.'
    },
    {
      question: 'How do I contact creators?',
      answer: 'You can message creators directly through our platform once they bid on your campaign or you can invite specific creators to your campaigns.'
    },
    {
      question: 'Can I cancel my subscription?',
      answer: 'Yes, you can cancel your subscription at any time from the Subscriptions section. Your account will remain active until the end of your current billing period.'
    }
  ];

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    try {
      // TODO: Implement API call to submit support request
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call

      alert('Your support request has been submitted. We will get back to you within 24 hours.');
      setContactForm({ subject: '', message: '', priority: 'normal' });
    } catch (error) {
      alert('Failed to submit request. Please try again.');
    }
  };

  const handleReportIssue = () => {
    setActiveTab('contact');
    setContactForm(prev => ({ ...prev, subject: 'Bug Report: ' }));
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <h2 className="section-title">Support Center</h2>
        <p className="section-description">Get help and submit support requests</p>
      </div>

      {/* Support Tabs */}
      <div className="support-tabs">
        <button
          className={`support-tab ${activeTab === 'help' ? 'active' : ''}`}
          onClick={() => setActiveTab('help')}
        >
          <HelpCircle size={18} />
          Help Center
        </button>
        <button
          className={`support-tab ${activeTab === 'contact' ? 'active' : ''}`}
          onClick={() => setActiveTab('contact')}
        >
          <MessageCircle size={18} />
          Contact Us
        </button>
        <button
          className={`support-tab ${activeTab === 'docs' ? 'active' : ''}`}
          onClick={() => setActiveTab('docs')}
        >
          <FileText size={18} />
          Documentation
        </button>
      </div>

      {/* Help Center Tab */}
      {activeTab === 'help' && (
        <div className="support-content">
          <h3 className="section-subtitle">Frequently Asked Questions</h3>

          <div className="faq-list">
            {faqItems.map((item, index) => (
              <details key={index} className="faq-item">
                <summary className="faq-question">
                  <HelpCircle size={18} className="text-blue-600" />
                  {item.question}
                </summary>
                <div className="faq-answer">
                  {item.answer}
                </div>
              </details>
            ))}
          </div>

          <div className="support-actions">
            <button onClick={handleReportIssue} className="btn-outline">
              <AlertCircle size={18} className="inline mr-2" />
              Report an Issue
            </button>
          </div>
        </div>
      )}

      {/* Contact Us Tab */}
      {activeTab === 'contact' && (
        <div className="support-content">
          <h3 className="section-subtitle">Contact Support</h3>

          <form onSubmit={handleContactSubmit} className="contact-form">
            <div className="form-group">
              <label htmlFor="subject" className="form-label">
                Subject *
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={contactForm.subject}
                onChange={handleContactChange}
                className="form-input"
                placeholder="Brief description of your issue"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="priority" className="form-label">
                Priority
              </label>
              <select
                id="priority"
                name="priority"
                value={contactForm.priority}
                onChange={handleContactChange}
                className="form-input"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="message" className="form-label">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                value={contactForm.message}
                onChange={handleContactChange}
                className="form-textarea"
                rows="6"
                placeholder="Describe your issue in detail..."
                required
              />
            </div>

            <button type="submit" className="btn-primary">
              <Send size={18} className="inline mr-2" />
              Submit Request
            </button>
          </form>

          <div className="contact-info">
            <h4 className="font-semibold mb-3">Other Ways to Reach Us</h4>
            <div className="contact-methods">
              <div className="contact-method">
                <p className="font-medium">Email</p>
                <a href="mailto:support@collaborb.com" className="text-blue-600">
                  support@collaborb.com
                </a>
              </div>
              <div className="contact-method">
                <p className="font-medium">Phone</p>
                <a href="tel:+916388178730" className="text-blue-600">
                  +91 6388178730
                </a>
              </div>
              <div className="contact-method">
                <p className="font-medium">Business Hours</p>
                <p className="text-gray-600">Monday - Friday, 9:00 AM - 6:00 PM IST</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Documentation Tab */}
      {activeTab === 'docs' && (
        <div className="support-content">
          <h3 className="section-subtitle">Documentation</h3>

          <div className="docs-grid">
            <div className="doc-card">
              <FileText size={32} className="text-blue-600 mb-3" />
              <h4 className="doc-title">Getting Started Guide</h4>
              <p className="doc-description">Learn the basics of using CollabOrb</p>
              <button className="btn-text">Read More →</button>
            </div>

            <div className="doc-card">
              <FileText size={32} className="text-green-600 mb-3" />
              <h4 className="doc-title">Campaign Management</h4>
              <p className="doc-description">How to create and manage campaigns</p>
              <button className="btn-text">Read More →</button>
            </div>

            <div className="doc-card">
              <FileText size={32} className="text-purple-600 mb-3" />
              <h4 className="doc-title">Analytics & Reports</h4>
              <p className="doc-description">Understanding your campaign metrics</p>
              <button className="btn-text">Read More →</button>
            </div>

            <div className="doc-card">
              <FileText size={32} className="text-orange-600 mb-3" />
              <h4 className="doc-title">Best Practices</h4>
              <p className="doc-description">Tips for successful collaborations</p>
              <button className="btn-text">Read More →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportSection;


